import { Dispatch, SetStateAction, useState } from "react";
import { useArtistContext } from "Components";
import IntegrationCard from "../IntegrationCard";
import styled, { CSSProperties } from "styled-components";
import { DateTime } from "luxon";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { Button } from "melodies-source/Button";
import { useDesktopMediaQuery } from "hooks";
import {
  OpenstageIntegrationModal,
  OpenstageRemovalModal,
} from "./OpenstageModals";
import LastSyncIcon from "../LastSyncIcon";
import { ReactComponent as LogoOpenstage } from "./openstage-header.svg";
import { Ref } from "refs";
import { FlexColumn } from "Components/Flex";

type IntegrationStatus = {
  isConnected: boolean;
  hasSynced: boolean;
  audienceConnected: boolean;
};

const OpenstageIntegration = ({ sx }: { sx?: CSSProperties }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [removalModalOpen, setRemovalModalOpen] = useState(false);

  const { id: artistGroupId } = useArtistContext();

  const [openstageDoc] = useDocumentData(
    Ref("artist_groups/{groupId}/artist_group_private/openstage", {
      groupId: artistGroupId,
    }),
  );

  const isConnected = !!openstageDoc?.secretPath;
  const hasSynced = !!openstageDoc?.lastSynced;
  const audienceConnected = !!openstageDoc?.audienceConnected;

  const lastSynced = hasSynced
    ? DateTime.fromMillis(openstageDoc.lastSynced.toMillis())
    : undefined;

  // Show "syncExistingContacts" step when API key exists but no external audience is connected
  const showSyncExistingContactsStep =
    isConnected && !audienceConnected ? "syncExistingContacts" : undefined;

  const status: IntegrationStatus = {
    isConnected,
    hasSynced,
    audienceConnected,
  };

  return (
    <>
      {modalOpen && (
        <OpenstageIntegrationModal
          artistGroupId={artistGroupId}
          isOpen
          onClose={() => setModalOpen(false)}
          overrideStatus={showSyncExistingContactsStep}
        />
      )}
      {removalModalOpen && (
        <OpenstageRemovalModal
          artistGroupId={artistGroupId}
          onClose={() => setRemovalModalOpen(false)}
          isOpen
        />
      )}
      <OpenstageIntegrationCard
        status={status}
        lastSynced={lastSynced}
        onRemoveOpenstage={() => setRemovalModalOpen(true)}
        setModalOpen={setModalOpen}
        sx={sx}
      />
    </>
  );
};

export default OpenstageIntegration;

const OpenstageIntegrationCard = ({
  status,
  lastSynced,
  onRemoveOpenstage,
  setModalOpen,
  sx,
}: {
  status: IntegrationStatus;
  lastSynced?: DateTime;
  onRemoveOpenstage: () => void;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
  sx?: CSSProperties;
}) => {
  const isDesktop = useDesktopMediaQuery();

  if (!status.isConnected) {
    return (
      <IntegrationCard
        header={<LogoOpenstage />}
        footer={
          <Button onClick={() => setModalOpen(true)}>
            Connect to Openstage
          </Button>
        }
        body="SET can automatically sync your contacts to your Openstage account. Click the button below to get started."
        sx={sx}
      />
    );
  }

  return (
    <IntegrationCard
      header={<LogoOpenstage />}
      body={
        <>
          {status.hasSynced && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <LastSyncIcon />
              <StatusText isDesktop={isDesktop}>
                Last Sync: {lastSynced?.toFormat("MMM dd, h:mm a ZZZZ")}
              </StatusText>
            </div>
          )}
          <BodyText>
            {status.audienceConnected
              ? "You're connected to Openstage!"
              : "You're connected, and any new contacts will be synced. To sync existing contacts, click below:"}
          </BodyText>
        </>
      }
      footer={
        <FlexColumn gap="12px" style={{ marginTop: 16 }}>
          {!status.audienceConnected && (
            <Button
              variant="outlined"
              compact
              onClick={() => setModalOpen(true)}
            >
              Sync Existing Contacts
            </Button>
          )}
          <FooterText
            style={{ textAlign: "center" }}
            onClick={() => onRemoveOpenstage()}
          >
            Remove Openstage
          </FooterText>
        </FlexColumn>
      }
      sx={sx}
    />
  );
};

const BodyText = styled.div`
  font-size: 10px;
  line-height: 170%;
  font-weight: 400;
  text-align: center;
  color: #666;
`;

const FooterText = styled.div`
  color: #180171;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0px;
  margin: 0 auto;
  padding-bottom: 5px;
  cursor: pointer;
  bottom: 0;
`;

const StatusText = styled.div<{ isDesktop: boolean }>`
  color: #635bff;
  font-weight: 500;
  font-size: ${({ isDesktop }) => (isDesktop ? "min(1.3vw, 12px)" : "13px")};
  line-height: 19.5px;
  margin-left: 0.25rem;
`;
